import { ModalName, modalsList } from '@features/ui-layout/components/Modal/modalsList';
import { cloneElement, createContext, ReactNode, useCallback, useContext, useMemo, useState, JSX } from 'react';

type Params<T extends ModalName> = Parameters<(typeof modalsList)[T]>[0];

type ModalContextValue = {
  closeModal: () => void;
  openModal: <T extends ModalName>(modalName: T, params?: Params<T>) => void;
};

const ModalContext = createContext<ModalContextValue | null>(null);

export const ModalProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [modal, setModal] = useState<ModalName | null>(null);
  const [params, setParams] = useState<Params<ModalName>>();

  const openModal = useCallback(
    <T extends ModalName>(modalName: T, modalParams?: Params<T>) => {
      setModal(modalName);
      setParams(modalParams);
    },
    [setModal],
  );

  const closeModal = useCallback(() => {
    setModal(null);
    setParams(undefined);
  }, []);

  const contextValue = useMemo(
    () => ({
      closeModal,
      openModal,
    }),
    [openModal, closeModal],
  );

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      {/* The cast is necessary otherwise TS complains even though typing is correct */}
      {modal &&
        params &&
        cloneElement(modalsList[modal](params), {
          onClose: closeModal,
          open: true,
        })}
    </ModalContext.Provider>
  );
};

export const useModal = (): ModalContextValue => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};
